import { useRouter } from 'next/router';
import { useCallback } from 'react';

import useUtmsFromSession from 'hooks/useUtmsFromSession';

import { sendEvent } from 'lib/gtag';
import {
  toCouponStoreWithCouponIdLink,
  toPromobitCouponsWithCouponIdLink,
  toRedirectCouponStoreLink,
} from 'lib/links';

import LINKS from 'constants/links';

/**
 * Some stores punish us when opening tabs in background to increase the
 * quantity of page views since this can "artificially" increase the amount of
 * our revenue. So, for contractual reasons we need to disable this behavior
 * only for these specific stores.
 */
const PROHIBITED_STORES_TO_OPEN_NEW_TABS = ['mercado-livre'];

const useCouponPicker = (gaEventCategory) => {
  const router = useRouter();
  const { utmParams } = useUtmsFromSession();

  const openCouponPicker = useCallback(
    ({ couponId, storeName, storeSlug }) => {
      sendEvent({
        action: 'pick_coupon',
        category: gaEventCategory,
        data: storeName,
      });

      const couponPickerUrl = toCouponStoreWithCouponIdLink(
        couponId,
        storeSlug
      );
      const isStoreProhibitedToOpenNewTabs =
        PROHIBITED_STORES_TO_OPEN_NEW_TABS.includes(storeSlug);

      if (isStoreProhibitedToOpenNewTabs) {
        router.push(couponPickerUrl, null, { shallow: true });
        return;
      }

      const storeLink =
        LINKS.PROMOBIT_COUPONS === router.asPath
          ? toPromobitCouponsWithCouponIdLink(couponId)
          : toCouponStoreWithCouponIdLink(couponId, storeSlug);

      window.open(storeLink, '_blank');
      window.open(toRedirectCouponStoreLink(couponId, utmParams), '_self');
    },
    [gaEventCategory, router]
  );

  return { openCouponPicker };
};

export default useCouponPicker;
